import React, { useEffect, useState } from 'react';
import { Card } from 'azure-devops-ui/Card';
import { Image } from 'azure-devops-ui/Image';
import { IVssContributedTab, TabBar, TabContent, TabProvider, TabSize } from 'azure-devops-ui/Tabs';
import { Button } from 'azure-devops-ui/Button';
import { ObservableArray, ObservableValue } from 'azure-devops-ui/Core/Observable';
import { Configuration } from '../clients/ogg';
import { Critic } from './agents/Critic';
import { StreamingSession } from '../services/streamingSession.service';
import { OggFieldService } from '../services/ogg.service';
import { Writer } from './agents/Writer';
import * as _ from 'lodash';
import { IAuthenticationProvider } from '../types';
import { useToast } from '../hooks/useToast';
import { useWorkItemManager } from '../hooks/useWorkItemManager';
import { Sherpa } from './agents/Sherpa';
import Disclaimer from './Disclaimer';
import { Rater } from './agents/Rater';
import { CommonService } from '../services/common.service';
import { useSettings } from '../hooks/useSettings';
import { Scribe } from './agents/scribe/Scribe';
import { useCachingService } from '../hooks/useCachingService';

export interface IActionSelectorProps {
  apiConfig: Configuration;
  oggService: OggFieldService;
  authManager: IAuthenticationProvider;
}

const latestTabCacheKey = 'oggLatestTab';

export default function ActionSelector(props: IActionSelectorProps) {
  const selectedTabId: ObservableValue<string> = new ObservableValue('critic');
  const [refresh, setRefresh] = useState(0);
  // const [showMistakesWarning, setshowMistakesWarning] = useState(true);
  const workItemManager = useWorkItemManager();
  const toastProvider = useToast();
  const settings = useSettings();
  const cache = useCachingService();
  const mailToLink = 'mailto:feedback@intriq.io?subject=Ogg Feedback';

  useEffect(() => {
    async function doWork() {
      console.debug('setting the latest tab');
      const latestTabId = await cache.getValue(latestTabCacheKey);
      onSelectedTabChanged(latestTabId ?? 'critic');
    }

    doWork();
  });

  console.debug('loading Action Selector');
  const streams: { [key: string]: StreamingSession } = {
    critic: new StreamingSession(
      'onStreamAnalyzeWorkItem',
      props.apiConfig,
      props.authManager,
      workItemManager,
      toastProvider
    ),
    writer: new StreamingSession(
      'onStreamMakeSuggestionBasedOnWorkItem',
      props.apiConfig,
      props.authManager,
      workItemManager,
      toastProvider
    ),
    sherpa: new StreamingSession(
      'onStreamSherpa',
      props.apiConfig,
      props.authManager,
      workItemManager,
      toastProvider
    ),
    rater: new StreamingSession(
      'onStreamRater',
      props.apiConfig,
      props.authManager,
      workItemManager,
      toastProvider
    ),
    scribe: new StreamingSession(
      'onStreamScribe',
      props.apiConfig,
      props.authManager,
      workItemManager,
      toastProvider
    ),
  };

  async function onSelectedTabChanged(newTabId: string) {
    selectedTabId.value = newTabId;
    cache.setValue(latestTabCacheKey, newTabId)
  }

  async function onRefreshClicked() {
    console.log('refreshing');

    for (let stream of _.values(streams)) await stream.cancel('Refresh button was clicked');

    setRefresh((oldValue) => oldValue + 1);
  }

  function onRenderRefreshButton() {
    return (
      <div className='flex-row rhythm-horizontal-8'>
        <Button
          text='Feedback'
          tooltipProps={{
            text: 'To make our product more effective for your organisation, we need your feedback: feedback@intriq.io',
          }}
          iconProps={{ iconName: 'Feedback' }}
          href={mailToLink}
          target='_blank'
        />
        {!settings.isWebApp && (
          <Button
            text='Settings'
            tooltipProps={{
              text: 'Go to the Ogg Project Settings area',
            }}
            iconProps={{ iconName: 'Settings' }}
            onClick={() => CommonService.goToProjectSettings()}
          />
        )}
        <Button
          text='Reset'
          primary={true}
          tooltipProps={{
            text: "Reset the agents' output to blank so that they can be run again.",
          }}
          iconProps={{ iconName: 'Refresh' }}
          onClick={onRefreshClicked}
        />
      </div>
    );
  }

  const providers = new ObservableArray<IVssContributedTab>();
  setTimeout(() => {
    providers.push(
      {
        id: 'critic',
        name: 'The Critic',
        className: 'persona-tab',
        iconProps: {
          render(className) {
            return <Image alt='Critic image' src={require('../img/TheCritic83x83.png')}></Image>;
          },
        },
        render: () => <Critic streamingSession={streams['critic']} oggService={props.oggService} />,
      },
      {
        id: 'sherpa',
        name: 'The Sherpa',
        className: 'persona-tab',
        render: () => <Sherpa streamingSession={streams['sherpa']} oggService={props.oggService} />,
        iconProps: {
          render(className) {
            return <Image alt='Sherpa image' src={require('../img/TheSherpa83x83.png')}></Image>;
          },
        },
      },
      {
        id: 'writer',
        name: 'The Writer',
        className: 'persona-tab',
        render: () => <Writer streamingSession={streams['writer']} oggService={props.oggService} />,
        iconProps: {
          render(className) {
            return <Image alt='Writer image' src={require('../img/TheWriter83x83.png')}></Image>;
          },
        },
      },
      {
        id: 'rater',
        name: 'The Rater',
        className: 'persona-tab',
        render: () => <Rater streamingSession={streams['rater']} oggService={props.oggService} />,
        iconProps: {
          render(className) {
            return <Image alt='Rater image' src={require('../img/TheRater83x83.png')}></Image>;
          },
        },
      },
      {
        id: 'scribe',
        name: 'The Scribe',
        className: 'persona-tab',
        render: () => <Scribe streamingSession={streams['scribe']} oggService={props.oggService} />,
        iconProps: {
          render(className) {
            return <Image alt='Scribe image' src={require('../img/TheScribe83x83.png')}></Image>;
          },
        },
      }
    );
  }, 1000);

  return (
    <div className='full-width rhythm-vertical-24 flex-column padding-bottom-8'>
      <Card className='flex-grow selector-card'>
        <div className='full-width flex-column'>
          <TabProvider providers={providers} selectedTabId={selectedTabId}>
            <TabBar
              onSelectedTabChanged={onSelectedTabChanged}
              selectedTabId={selectedTabId}
              tabSize={TabSize.Tall}
              renderAdditionalContent={onRenderRefreshButton}
              className='full-width flex-column'
            ></TabBar>
            <TabContent />
          </TabProvider>
        </div>
      </Card>
      <Disclaimer />
    </div>
  );
}
